<template>
    <Tag
        v-if="visible"
        v-tooltip.top="tooltipText"
        :severity="severity"
        :icon="icon"
        :value="label"
        class="email-deliverability-badge"
    />
</template>

<script>
import Tag from 'primevue/tag';
import Tooltip from 'primevue/tooltip';

export default {
    name: 'EmailDeliverabilityBadge',
    components: { Tag },
    directives: { tooltip: Tooltip },
    props: {
        /**
         * One of:
         *   'OK' | null | undefined  -> nothing renders
         *   'BOUNCED_SOFT'           -> amber, "Soft bounce"
         *   'BOUNCED_HARD'           -> red,   "Bounced"
         *   'COMPLAINED'             -> red,   "Spam complaint"
         *   'SUPPRESSED'             -> red,   "Suppressed"
         */
        status: { type: String, default: null },
        lastBouncedAt: { type: [String, Date], default: null },
        lastDiagnostic: { type: String, default: null }
    },
    computed: {
        visible() {
            return this.status && this.status !== 'OK';
        },
        severity() {
            if (this.status === 'BOUNCED_SOFT') return 'warning';
            return 'danger';
        },
        icon() {
            if (this.status === 'COMPLAINED') return 'pi pi-flag-fill';
            if (this.status === 'BOUNCED_SOFT') return 'pi pi-exclamation-circle';
            return 'pi pi-exclamation-triangle';
        },
        label() {
            switch (this.status) {
                case 'BOUNCED_SOFT': return 'Soft bounce';
                case 'BOUNCED_HARD': return 'Bounced';
                case 'COMPLAINED':   return 'Spam complaint';
                case 'SUPPRESSED':   return 'Suppressed';
                default:             return this.status;
            }
        },
        formattedDate() {
            if (!this.lastBouncedAt) return null;
            try {
                const d = new Date(this.lastBouncedAt);
                if (isNaN(d.getTime())) return null;
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            } catch (_) {
                return null;
            }
        },
        tooltipText() {
            const parts = [];
            const dateText = this.formattedDate ? `Last bounced ${this.formattedDate}` : 'Address is on the suppression list';
            parts.push(dateText);
            if (this.lastDiagnostic) parts.push(`Reason: ${this.lastDiagnostic}`);
            parts.push(this.status === 'BOUNCED_SOFT'
                ? 'Repeated soft bounces will surface this contact in the Outdated Vendor Partner Emails report.'
                : 'Update the email or delete this contact to restore deliverability.');
            return parts.join(' — ');
        }
    }
};
</script>

<style scoped>
.email-deliverability-badge {
    margin-left: 8px;
    cursor: help;
}
</style>